

import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axiosApi from '../server/service';
import { User as UserIcon } from 'lucide-react';
import toast from "react-hot-toast";

import {
    Input, Button, Badge,
    ShadcnTable, ShadcnTableHeader, ShadcnTableBody, ShadcnTableRow, ShadcnTableHead, ShadcnTableCell,
   UserActionMenu
} from "../component/ui/TableComponent";

import {
  Copy,
  Package,
  ArrowLeft,
  Plus,
  Search,
  Trash2,
  Loader2,
  Pen,
  MoreVertical,
} from "lucide-react";


interface ApiUser {
  _id: string;
  username: string;
  fullName?: string;
  userRole: "admin" | "supervisor" | "employee";
  employeeType?: "Karigar" | "Master"|"job-work" | "Group Work";
  status: "Active" | "Inactive";
  createdAt?: string;
  updatedAt?: string;
}

interface UserData {
  id: string;
  username: string;
  name: string;
  employeeType?: string;
  role: string;
  status: "Active" | "Inactive";
  lastUpdated: string;
}


const formatDateTime = (iso?: string) => (iso ? new Date(iso).toLocaleString('en-GB') : "—");




const UserManagement: React.FC = () => {
  const navigate = useNavigate();

  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState<string>("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [itemToDelete, setItemToDelete] = useState<{ id: string; name: string } | null>(null);

  const totalPages = Math.ceil(totalCount / pageSize);

  const fetchUsers = useCallback(async (pageToFetch: number) => {
    setLoading(true);
    setFetchError(null);
    const params = new URLSearchParams({ page: String(pageToFetch), limit: String(pageSize) });
    if (roleFilter !== "all") params.append("role", roleFilter);
    if (statusFilter !== "all") params.append("status", statusFilter);
    if (searchInput) params.append("search", searchInput);

    try {
      const { data } = await axiosApi.get(`/api/users/get-users?${params.toString()}`);
      const mapped: UserData[] = data.users.map((u: ApiUser) => ({
        id: u._id,
        username: u.username,
        name: u.fullName || u.username,
        role: u.userRole,
        employeeType: u.employeeType,
        status: u.status,
        lastUpdated: formatDateTime(u.updatedAt || u.createdAt),
      }));
      setUsers(mapped);
      setTotalCount(data.totalCount);
    } catch (err: any) {
      setFetchError(err?.response?.data?.message || "Failed to load users");
      setUsers([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [pageSize, roleFilter, statusFilter, searchInput]);


  const confirmDelete = useCallback(async () => {
    if (!itemToDelete) return;
    try {
      await axiosApi.delete(`/api/users/delete-user/${itemToDelete.id}`);
      toast.success(`User ${itemToDelete.name} deleted successfully.`);
      setTotalCount(prev => prev - 1);
      if (users.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchUsers(currentPage);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to delete user.");
    } finally {
      setItemToDelete(null);
    }
  }, [itemToDelete, currentPage, users.length, fetchUsers]);
  
  const handleEdit = (userId: string) => {
    navigate(`/edit-user/${userId}`);
  };


  useEffect(() => {
    const handler = setTimeout(() => {
        if(currentPage !== 1) setCurrentPage(1);
        else fetchUsers(1);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchInput, roleFilter, statusFilter]);

  // useEffect(() => {
  //   fetchUsers(currentPage);
  // }, [currentPage]);

  useEffect(() => {
    if (currentPage > 0) { // To avoid initial double fetch on filter change
        fetchUsers(currentPage);
    }
  }, [currentPage, fetchUsers]);

  return (
    <div className="bg-slate-50 min-h-screen  font-sans">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-6">
        <div className="">

          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-800">
              User Management
            </h1>
            <p className="text-slate-600 text-sm sm:text-base mt-1">
              Manage users and their permissions
            </p>
          </div>
        </div>
        <Link
          to="/createuser"
          className="w-full md:w-auto flex items-center justify-center text-white bg-green-500 hover:bg-green-600 px-4 py-3 rounded-lg gap-2 transition-colors"
        >
          <Plus className="w-5 h-5" />
          <span className="font-medium">New User</span>
        </Link>
      </div>

      {/* Filters */}
      <div className="mt-6 bg-white shadow-md rounded-lg p-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search by Full Name or Username..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="md:col-span-2 grid sm:grid-cols-2 gap-4">
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="w-full h-10 border border-slate-300 rounded-md px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
            >
              <option value="all">All Roles</option>
              <option value="admin">Admin</option>
              <option value="supervisor">Supervisor</option>
              <option value="employee">Employee</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full h-10 border border-slate-300 rounded-md px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
            >
              <option value="all">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Data Display */}
      <div className="mt-6 bg-white shadow-lg rounded-lg">
        {loading ? (
          <div className="flex items-center justify-center gap-2 p-12 text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span>Loading Users...</span>
          </div>
        ) : fetchError ? (
          <div className="p-12 text-center text-red-600 font-medium">
            {fetchError}
          </div>
        ) : users.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            No users found matching your criteria.
          </div>
        ) : (
          <>
            {/* Desktop Table */}

            <div className="hidden lg:block">
              <ShadcnTable>
                <ShadcnTableHeader>
                  <ShadcnTableRow>
                    <ShadcnTableHead>Full Name</ShadcnTableHead>
                    <ShadcnTableHead>Username</ShadcnTableHead>
                    <ShadcnTableHead>Role</ShadcnTableHead>
                    <ShadcnTableHead>Status</ShadcnTableHead>
                    <ShadcnTableHead>Last Updated</ShadcnTableHead>
                    <ShadcnTableHead className="text-center">
                      Actions
                    </ShadcnTableHead>
                  </ShadcnTableRow>
                </ShadcnTableHeader>
                <ShadcnTableBody>
                  {users.map((user) => (
                    <ShadcnTableRow key={user.id}>
                      <ShadcnTableCell className="font-medium text-slate-900">
                        {user.name}
                      </ShadcnTableCell>
                      <ShadcnTableCell>{user.username}</ShadcnTableCell>
                      <ShadcnTableCell>
                        {" "}
                        <div>{user.role}</div>
                        {user.role === "employee" && user.employeeType && (
                          <div className="text-sm text-gray-500">
                            • {user.employeeType}
                          </div>
                        )}
                      </ShadcnTableCell>
                      <ShadcnTableCell>
                        <Badge
                          variant={
                            user.status === "Active" ? "success" : "danger"
                          }
                        >
                          {user.status}
                        </Badge>
                      </ShadcnTableCell>
                      <ShadcnTableCell>
                        {user.lastUpdated}
                      </ShadcnTableCell>
                      <ShadcnTableCell className="flex justify-center">
                        <UserActionMenu 
                          user={user}
                          onDelete={(id, name) => setItemToDelete({ id, name })}
                          onEdit={handleEdit}
                        />
                      </ShadcnTableCell>
                    </ShadcnTableRow>
                  ))}
                </ShadcnTableBody>
              </ShadcnTable>
            </div>

            {/* Mobile Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 lg:hidden">
              {users.map((user) => (
                <div
                  key={user.id}
                  className="bg-white rounded-lg shadow-md border border-gray-300 p-4 flex flex-col justify-between"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 bg-slate-100 p-3 rounded-full">
                        <UserIcon className="text-gray-500 w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg text-slate-900">
                          {user.name}
                        </h3>
                        <p className="text-sm text-slate-600">
                          @{user.username}
                        </p>
                      </div>
                    </div>
                    <UserActionMenu 
                      user={user}
                      onDelete={(id, name) => setItemToDelete({ id, name })}
                      onEdit={handleEdit}
                    />
                  </div>
                  <div className="mt-4 space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500">Status:</span>
                      <Badge
                        variant={
                          user.status === "Active" ? "success" : "danger"
                        }
                      >
                        {user.status}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Role:</span>
                      <span className="font-medium text-slate-800">
                        {user.role}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Last Updated:</span>
                      <span className="font-medium text-slate-800">
                        {user.lastUpdated}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between flex-wrap gap-4 p-4 border-t border-gray-300">
                <span className="text-sm text-slate-600">
                  Showing <span className="font-medium">{users.length}</span> of{" "}
                  <span className="font-medium">{totalCount}</span> results
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage((p) => p - 1)}
                    disabled={currentPage === 1 || loading}
                  >
                    Previous
                  </Button>
                  <span className="text-sm font-medium px-2">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage((p) => p + 1)}
                    disabled={currentPage >= totalPages || loading}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {itemToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md m-4 p-6">
            <h2 className="text-lg font-semibold text-slate-900">
              Confirm Deletion
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              Are you sure you want to delete user:{" "}
              <span className="font-bold">{itemToDelete.name}</span>? This
              action cannot be undone.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setItemToDelete(null)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={confirmDelete}>
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;